import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../../components/ui/select";
import AssetVersionSlotField from "../../components/AssetVersionSlotField";
import { SLOT_BY_KEY, SLOT_KEY_BY_FAMILY } from "../compositionSlots";

/**
 * EmailBuilderProperties — EMAILS-IMPL-015
 *
 * The RIGHT card: contextual to the selected Canvas block (§22-24). Asset
 * identity/Version selection reuses AssetVersionSlotField unchanged --
 * the operator is choosing which governed Asset+exact-Version this block
 * pins, never rewriting the Asset's own canonical content from here
 * (§23). A version/asset change updates `fingerprint` and the Canvas
 * block together in one transition (owned by the parent's
 * `updateBlockRef`) -- this component only surfaces the control.
 *
 * Image presentation exposes exactly the existing governed V1 fields
 * (fingerprint.image_presentation) -- never a second CanvasBlock-owned
 * presentation representation (CRE-SPEC-001 §15.7).
 */
export default function EmailBuilderProperties({ draft, selectedBlockId, readOnly, onChangeRef, onChangeImagePresentation, onAddCtaEcho, hasCtaEcho }) {
  const block = draft.canvasLayout.find((b) => b.blockId === selectedBlockId);

  return (
    <Card className="glass flex h-full flex-col border-white/10" data-testid="builder-properties">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-slate-100">Properties</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto pt-0">
        {!block ? (
          <p className="text-sm text-slate-500" data-testid="builder-properties-empty">
            Select a Canvas block to view or edit its governed reference.
          </p>
        ) : (
          <PropertiesForBlock
            block={block}
            readOnly={readOnly}
            onChangeRef={onChangeRef}
            onChangeImagePresentation={onChangeImagePresentation}
            imagePresentation={draft.fingerprint.imagePresentation}
            onAddCtaEcho={onAddCtaEcho}
            hasCtaEcho={hasCtaEcho}
          />
        )}
      </CardContent>
    </Card>
  );
}

function PropertiesForBlock({ block, readOnly, onChangeRef, onChangeImagePresentation, imagePresentation, onAddCtaEcho, hasCtaEcho }) {
  const slot = SLOT_BY_KEY[SLOT_KEY_BY_FAMILY[block.family]];
  const isEcho = block.role === "echo";

  return (
    <div className="space-y-4" data-testid={`builder-properties-block-${block.blockId}`}>
      <div className="space-y-1">
        <Label>Family</Label>
        <p className="text-sm text-slate-300" data-testid="builder-properties-family">
          {slot.label}
          {block.role === "primary" && " (Primary)"}
          {isEcho && " (Echo)"}
        </p>
      </div>

      {isEcho ? (
        <div className="rounded-md border border-white/10 bg-black/20 p-2 text-sm text-slate-400" data-testid="builder-properties-echo-note">
          A CTA echo always matches the primary CTA's exact Asset and Version — {block.assetId} @ {block.version}. It cannot be changed independently.
        </div>
      ) : readOnly ? (
        <div className="rounded-md border border-white/10 bg-black/20 p-2 font-mono text-sm text-slate-300" data-testid="builder-properties-readonly-ref">
          {block.assetId} @ {block.version}
        </div>
      ) : (
        <AssetVersionSlotField
          label={slot.label}
          required
          value={{ assetId: block.assetId, version: block.version }}
          onChange={(value) => value && onChangeRef(block.blockId, value)}
          listFn={slot.listFn}
          versionsFn={slot.versionsFn}
          idField={slot.idField}
          eligibleStatuses={slot.eligibleStatuses}
          testPrefix="builder-properties-ref"
        />
      )}

      {block.family === "CTA" && block.role !== "echo" && (
        <div className="space-y-2 border-t border-white/10 pt-3">
          <p className="text-xs text-slate-500">Final tracking URL is generated at Broadcast handoff.</p>
          {!readOnly && !hasCtaEcho && (
            <Button type="button" size="sm" variant="outline" className="border-white/10 text-slate-200" onClick={onAddCtaEcho} data-testid="builder-add-cta-echo">
              Add CTA Echo
            </Button>
          )}
        </div>
      )}

      {block.family === "IMG" && !readOnly && (
        <ImagePresentationFields value={imagePresentation} onChange={onChangeImagePresentation} />
      )}
    </div>
  );
}

function ImagePresentationFields({ value, onChange }) {
  const v = value || { placement: "", width: "", alignment: "" };
  const update = (patch) => onChange({ ...v, ...patch });

  return (
    <div className="space-y-3 border-t border-white/10 pt-3">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Image Presentation</div>
      <div className="space-y-1.5">
        <Label htmlFor="builder-image-placement">Placement</Label>
        <Input id="builder-image-placement" value={v.placement || ""} onChange={(e) => update({ placement: e.target.value })} placeholder="e.g. header" data-testid="builder-image-placement-input" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="builder-image-width">Display Width (px)</Label>
        <Input id="builder-image-width" type="number" value={v.width ?? ""} onChange={(e) => update({ width: e.target.value })} placeholder="e.g. 600" data-testid="builder-image-width-input" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="builder-image-alignment">Alignment</Label>
        <Select value={v.alignment || ""} onValueChange={(alignment) => update({ alignment })}>
          <SelectTrigger id="builder-image-alignment" data-testid="builder-image-alignment-select">
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left" data-testid="builder-image-alignment-option-left">Left</SelectItem>
            <SelectItem value="center" data-testid="builder-image-alignment-option-center">Center</SelectItem>
            <SelectItem value="right" data-testid="builder-image-alignment-option-right">Right</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
